import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Search,
  Briefcase,
  Users,
  Phone,
  Wrench,
  ShoppingCart,
  Fuel,
} from 'lucide-react';
import { cn } from '../lib/utils';

// Matches the original My Key Boss bottom-nav: 8 quick-access items.
// Vehicle Lookup is intentionally placed 2nd so it's always one tap away.
const NAV_ITEMS = [
  { path: '/', label: 'Home', icon: LayoutDashboard },
  { path: '/vehicle-lookup', label: 'Vehicle', icon: Search },
  { path: '/jobs', label: 'Jobs', icon: Briefcase },
  { path: '/customers', label: 'CRM', icon: Users },
  { path: '/phone-system', label: 'Phone', icon: Phone },
  { path: '/equipment', label: 'Tools', icon: Wrench },
  { path: '/inventory', label: 'Parts', icon: ShoppingCart },
  { path: '/gas-expense', label: 'Gas', icon: Fuel },
];

// Preserve scroll position per route, matching the original behavior.
const scrollPositions = {};

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      const main = document.querySelector('main');
      if (main) scrollPositions[prevPathRef.current] = main.scrollTop;
      prevPathRef.current = location.pathname;
      // Restore on next tick after content mounts
      setTimeout(() => {
        if (main) main.scrollTop = scrollPositions[location.pathname] || 0;
      }, 0);
    }
  }, [location.pathname]);

  const handleClick = (path) => {
    if (location.pathname === path) {
      const main = document.querySelector('main');
      if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(path);
  };

  return (
    <nav
      className="shrink-0 bg-card/95 backdrop-blur-md border-t border-yellow-500/30 shadow-2xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="bottom-nav"
    >
      <div className="flex items-center justify-around px-1 py-1.5 max-w-5xl mx-auto">
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
          const isActive = location.pathname === path;
          return (
            <button
              key={path}
              onClick={() => handleClick(path)}
              className={cn(
                'flex flex-col items-center gap-0.5 py-1 px-2 rounded-lg flex-1 active:scale-95 transition-all duration-150',
                isActive ? 'text-yellow-400' : 'text-muted-foreground hover:text-foreground'
              )}
              style={{ minHeight: '52px', minWidth: '44px', touchAction: 'manipulation' }}
              data-testid={`bottom-nav-${label.toLowerCase()}`}
            >
              <Icon className="h-5 w-5" />
              <span className="text-[10px] font-medium leading-tight">{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
